import React, { useMemo } from "react";
import {
  Button,
  ChevronBottomIcon,
  DownloadIcon,
  Pagination,
} from "@pd/pd-ui-kit";
import { format, isValid } from "date-fns";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import IconInfo from "~/components/Icons/info";
import LicenseActionsModals from "~/features/LicenseActionsModals";
import Popover from "~/shared/ui/Fields/Popover";
import Wrapper from "~/shared/ui/Wrapper";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { getAvailabilityColor } from "~/shared/lib/licenseAvailability";
import { TableV2, useURLTableSort } from "~/shared/ui/TableV2";
import { InfoTooltip } from "~/shared/ui/Tooltip";

import LicensesFilter from "./LicensesFilter";
import {
  getLicensesColumns,
  LICENSES_SORTABLE_COLUMNS,
} from "./model/licensesColumns";
import {
  ILicensesUrlParams,
  parseLicensesFilterFromUrl,
} from "./model/licensesUrlParams";
import { mapLicensesFilterToQuery } from "./model/mapLicensesFilterToQuery";
import { ILicenseSummaryRow, LICENSES_TOTALS_ROW_ID } from "./model/types";
import { useLicenseOrganizations } from "./model/useLicenseOrganizations";

import s from "./TrainingManagement.less";

const TrainingManagement = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const setParams = useURLParamsSetter();
  const urlParams = useURLParamsGetter<ILicensesUrlParams>();
  const { page, count = 25, sortField, sortType: sortTypeRaw } = urlParams;
  const { notifyWarning } = useNotify();
  const { hasPermission, hasSomePermission } = usePermissions();

  const canAssignLicense = hasPermission(
    "employeeAccounting.licenses.assign.post",
  );
  const canRevokeLicense = hasSomePermission([
    "employeeAccounting.licenses.revoke.post",
    "employeeAccounting.licenses.mass-revoke.post",
  ]);

  const { sortType: normalizedSortType, onSortChange } = useURLTableSort({
    sortField: sortField ? String(sortField) : undefined,
    sortType: sortTypeRaw ? String(sortTypeRaw) : undefined,
    sortableColumns: LICENSES_SORTABLE_COLUMNS,
    setParams,
  });

  const tableSortField =
    sortField && LICENSES_SORTABLE_COLUMNS.includes(String(sortField))
      ? String(sortField)
      : undefined;

  const formFilter = useMemo(
    () => parseLicensesFilterFromUrl(urlParams),
    [urlParams],
  );

  const pageNumber = Number(page) || 0;
  const pageSize = Number(count) || 25;

  const queryParams = useMemo(
    () =>
      mapLicensesFilterToQuery(
        pageNumber,
        pageSize,
        formFilter,
        tableSortField,
        normalizedSortType,
      ),
    [pageNumber, pageSize, formFilter, tableSortField, normalizedSortType],
  );

  const { rows, total, statusTotals, pool, isLoading } =
    useLicenseOrganizations(queryParams);

  const poolLegend = useMemo(() => {
    if (!pool) {
      return undefined;
    }

    const syncedDate = pool.syncedAt ? new Date(pool.syncedAt) : null;

    return t("trainingManagement.table.poolLegend", {
      limit: pool.limit,
      added: pool.added,
      loggedIn: pool.loggedIn,
      active: pool.active,
      blocked: pool.blocked,
      free: pool.free,
      syncedAt:
        syncedDate && isValid(syncedDate)
          ? format(syncedDate, "dd.MM.yyyy HH:mm")
          : "—",
    });
  }, [t, pool]);

  const columns = useMemo(
    () =>
      getLicensesColumns(
        t,
        tableSortField,
        normalizedSortType,
        formFilter.licenseType,
      ),
    [t, tableSortField, normalizedSortType, formFilter.licenseType],
  );

  const totalsRow = useMemo<ILicenseSummaryRow | null>(() => {
    if (!statusTotals) {
      return null;
    }

    return {
      id: LICENSES_TOTALS_ROW_ID,
      dealerId: null,
      organizationId: null,
      organizationName: "",
      inn: "",
      branchCode: "",
      dealerName: "",
      partnerName: "",
      city: "",
      region: "",
      country: "",
      federalDistrict: "",
      availabilityColor: getAvailabilityColor(
        statusTotals.available,
        statusTotals.total,
      ),
      totalQuantity: statusTotals.total,
      assignedQuantity: statusTotals.assigned,
      usedQuantity: statusTotals.used,
      frozenQuantity: statusTotals.frozen,
      freeQuantity: statusTotals.available,
      expiredQuantity: statusTotals.expired,
    };
  }, [statusTotals]);

  const tableRows = useMemo(
    () => (totalsRow ? [totalsRow, ...rows] : rows),
    [totalsRow, rows],
  );

  const manageMenuItems = useMemo(() => {
    const items: { label: string; value: string }[] = [];

    if (canAssignLicense) {
      items.push({
        label: t("trainingManagement.assign"),
        value: "LicenseAssign",
      });
    }

    if (canRevokeLicense) {
      items.push({
        label: t("trainingManagement.revoke"),
        value: "LicenseRevoke",
      });
    }

    return items;
  }, [t, canAssignLicense, canRevokeLicense]);

  const handleManageSelect = (item: { value: string }) => {
    setParams({ openModal: item.value });
  };

  const { handleCount, handlePage } = usePaginationParams("urlParams");

  const goBack = () => {
    history.goBack();
  };

  const handleExportExcel = () => {
    notifyWarning(t("trainingManagement.notification.excelUnavailable"));
  };

  const openOrganization = (record: ILicenseSummaryRow) => {
    const params = new URLSearchParams();

    if (record.dealerId != null) {
      params.set("dealerId", String(record.dealerId));
    }

    if (record.organizationId) {
      params.set("organizationId", record.organizationId);
    }

    params.set("organizationName", record.organizationName);

    if (formFilter.licenseType !== "all") {
      params.set("licenseType", formFilter.licenseType);
    }

    window.open(`/staff/licenses/organization?${params.toString()}`, "_blank");
  };

  return (
    <Wrapper.Content
      title={t("trainingManagement.title")}
      txtBtn={t("ui.back")}
      goBackHandler={goBack}
      controlComponent={
        <div className={s.headerActions}>
          {manageMenuItems.length > 0 && (
            <Popover
              trigger="click"
              content={manageMenuItems}
              menuTitle=""
              matchTriggerWidth
              showSelected={false}
              onSelect={handleManageSelect}
            >
              <Button
                bold
                color="secondary"
                variant="filled"
                fontSize="12"
                size="medium"
                icon={<ChevronBottomIcon size={12} />}
              >
                {t("trainingManagement.manageLicenses")}
              </Button>
            </Popover>
          )}
          {hasPermission("employeeAccounting.partners.get") && (
            <Button
              bold
              color="secondary"
              fontSize="12"
              size="medium"
              onClick={() => history.push("/staff/licenses/partners")}
            >
              {t("trainingManagement.partners")}
            </Button>
          )}
          <Button
            bold
            color="secondary"
            fontSize="12"
            size="medium"
            onClick={() => history.push("/staff/licenses/requests")}
          >
            {t("trainingManagement.licenseManagement")}
          </Button>
        </div>
      }
      filterComponent={<LicensesFilter />}
    >
      <div className={s.downloadToolbar}>
        <Button
          bold
          color="secondary"
          variant="filled"
          fontSize="10"
          size="small"
          radius={30}
          icon={<DownloadIcon size={12} />}
          onClick={handleExportExcel}
        >
          {t("trainingManagement.downloadExcel")}
        </Button>
        {pool && poolLegend ? (
          <span className={s.poolFree}>
            <span>
              {t("trainingManagement.table.free")} {pool.free}
            </span>
            <InfoTooltip
              content={poolLegend}
              placement="bottomRight"
              minWidth={0}
            >
              <span className={s.poolDetails}>
                {t("trainingManagement.table.poolDetails")}
                <IconInfo width="16" height="16" fill="#f17830" />
              </span>
            </InfoTooltip>
          </span>
        ) : null}
      </div>

      <TableV2
        wrapperClassName={`${s.tableWrapper} ${s.trainingTable}`}
        columns={columns}
        dataSource={tableRows}
        rowKey="id"
        loading={isLoading}
        sortableColumns={LICENSES_SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortField ? normalizedSortType : undefined}
        onSortChange={onSortChange}
        rowDecorator={(record) =>
          record.id === LICENSES_TOTALS_ROW_ID ? { className: s.totalsRow } : {}
        }
        onRow={(record) =>
          record.id === LICENSES_TOTALS_ROW_ID
            ? {}
            : {
                onClick: () => openOrganization(record),
                style: { cursor: "pointer" },
              }
        }
      />

      <Pagination
        count={pageSize}
        page={pageNumber + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
        selectFrom={[25, 50, 100]}
      />

      <LicenseActionsModals />
    </Wrapper.Content>
  );
};

export default TrainingManagement;
